import React, { useState, FormEvent, useEffect } from 'react'
import { formatOptionLabel } from '../../formatters'
import useDisclosure from '../../hooks/useDisclosure'
import { labels } from '../../labels'
import OutlinedInput from '../outlinedInput/OutlinedInput'
import { iconNames } from '../svgIcon/SvgIcon'
import styles from './AsyncSelectFromApi.module.css'

interface IAsyncSelectFromApiProps {
    searchFunctionByString: (newValue: string) => Promise<any>
    objectToOption: (objects: any[]) => Option[]
    floatingLabel: string
    placeholder?: string
    formId?: string
    errorMessage?: string
    initValue?: string
    onSelect: (newValue: string | null) => void
    disabled?: boolean
    empty?: boolean
    emptyLabel?: string
    keepFirstPart?: boolean
}

interface IMenuProps {
    options: Option[]
    onSelect: (option: Option) => void
    empty?: boolean
    emptyLabel?: string
}

function Menu(props: IMenuProps) {
    if (props.options.length === 0 && !props.empty) {
        return null
    }

    if (props.options.length === 0 && props.empty && props.emptyLabel) {
        return <div className={styles.emptyMessage}>{props.emptyLabel}</div>
    }

    return (
        <div className={styles.menuContainer}>
            {props.options.map((element) => (
                <div
                    key={element.id}
                    onClick={() => props.onSelect(element)}
                    className={styles.menuItem}
                >
                    <div className={styles.textMenuItemSuggest}>
                        {element.label}
                    </div>
                </div>
            ))}
            {props.options.length === 0 && props.empty && props.emptyLabel && (
                <div className={styles.emptyMessage}>{props.emptyLabel}</div>
            )}

            {props.options.length === 0 && !props.empty && (
                <div className={styles.menuItem}>{labels.optionNotFound}</div>
            )}
        </div>
    )
}

export default function AsyncSelectFromApi(props: IAsyncSelectFromApiProps) {
    const [searchValue, setSearchValue] = useState<string>('')
    const [foundedOptions, setFoundedOptions] = useState<Option[]>([])
    const [optionSelected, setOptionSelected] = useState<Option | null>(null)
    const {
        open: focused,
        handleOpen: onFocus,
        handleClose: onBlur,
    } = useDisclosure()
    const {
        open: openMenu,
        handleOpen: handleOpenMenu,
        handleClose: handleCloseMenu,
    } = useDisclosure()

    const handleChangeSearch = async (event: FormEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.value

        setOptionSelected(null)
        setSearchValue(newValue)
        if (newValue.length < 3) {
            setFoundedOptions([])
            props.onSelect(null)
            return null
        }
        const result = await props.searchFunctionByString(newValue)
        const options = props.objectToOption(result)
        const founded = options.filter((element) =>
            formatOptionLabel(element.label).includes(
                formatOptionLabel(searchValue)
            )
        )

        setFoundedOptions(founded || [])
        if (founded.length > 0) {
            handleOpenMenu()
        }
    }

    const handleSelect = (option: Option) => {
        setSearchValue('')
        setOptionSelected(option)
        handleCloseMenu()
        onBlur()
    }

    const handleBlur = () => {
        setTimeout(() => {
            onBlur()
        }, 200)
    }
    useEffect(() => {
        if (focused && searchValue.length > 3) {
            handleOpenMenu()
        } else {
            handleCloseMenu()
        }
    }, [searchValue, focused])

    const valueToShow = () => {
        if (!optionSelected) {
            return searchValue
        }
        if (props.keepFirstPart) {
            return optionSelected.label.split(' ')[0]
        }
        return optionSelected.label
    }

    useEffect(() => {
        if (optionSelected) {
            props.onSelect(optionSelected.fullObject)
        } else {
            props.onSelect(null)
        }
    }, [optionSelected])

    const showNoValid =
        !props.empty && searchValue && !optionSelected
            ? labels.noValidOption
            : ''

    useEffect(() => {
        const optionFounded = foundedOptions.find(
            (element) => element.id === props.initValue
        )
        if (props.initValue && optionFounded) {
            setOptionSelected(optionFounded)
        }
    }, [props.initValue])

    return (
        <div onClick={onFocus} onBlur={handleBlur}>
            <OutlinedInput
                formId={props.formId}
                onChange={handleChangeSearch}
                floatingLabel={props.floatingLabel}
                placeholder={props.placeholder}
                trailingIcon={iconNames.search}
                value={valueToShow()}
                error={
                    !optionSelected && !searchValue
                        ? props.errorMessage
                        : showNoValid
                }
                disabled={props.disabled}
            />
            {openMenu && (
                <Menu
                    options={foundedOptions}
                    onSelect={handleSelect}
                    empty={props.empty}
                    emptyLabel={props.emptyLabel}
                />
            )}
        </div>
    )
}
